import { Button, ButtonProps } from '@heroui/react';
import React from 'react';

export type ButtonVariant =
  | 'solid'
  | 'bordered'
  | 'light'
  | 'flat'
  | 'faded'
  | 'shadow';
export type ButtonColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'danger';

export interface BaseButtonProps extends Omit<
  ButtonProps,
  'variant' | 'color'
> {
  /**
   * The variant of the button
   * @default "solid"
   */
  variant?: ButtonVariant;

  /**
   * The color of the button
   * @default "primary" for confirm actions, "default" for cancel/secondary actions
   */
  color?: ButtonColor;

  /**
   * Whether this is a confirm/primary action button
   * @default false
   */
  isConfirmButton?: boolean;

  /**
   * Whether this is a destructive action button
   * @default false
   */
  isDestructive?: boolean;
}

export const BaseButton: React.FC<BaseButtonProps> = ({
  variant,
  color,
  isConfirmButton = false,
  isDestructive = false,
  isDisabled,
  className = '',
  ...props
}) => {
  // Default styles based on button type
  const getDefaultStyles = (): {
    variant: ButtonVariant;
    color: ButtonColor;
  } => {
    if (isDestructive) {
      return {
        variant: variant || 'solid',
        color: 'danger',
      };
    }

    if (isConfirmButton) {
      return {
        variant: variant || 'solid',
        color: color || 'primary',
      };
    }

    return {
      variant: variant || 'flat',
      color: color || 'default',
    };
  };

  const { variant: finalVariant, color: finalColor } = getDefaultStyles();

  return (
    <Button
      {...props}
      variant={finalVariant}
      color={finalColor}
      isDisabled={isDisabled}
      className={`font-medium transition-all duration-200 ${
        isDisabled
          ? 'opacity-50 cursor-not-allowed pointer-events-none'
          : 'hover:opacity-90 active:scale-[0.98]'
      } ${className}`}
    />
  );
};
